"use client";

import dynamic from "next/dynamic";
import { useState, type FocusEvent, type PointerEvent } from "react";

import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";
import type { VimshottariLord } from "@/lib/api/vimshottari";

import {
  RUNNING_ANTARDASHA_FIELD_IDS,
  RUNNING_ANTARDASHA_FORM,
  RUNNING_ANTARDASHA_FRAME,
} from "./running-antardasha-form";

// The Hindi antardasha check's boundary, the planet-page check's pattern
// (RunningDashaCheckLazy, #2460): the server page imports only this module,
// and it owns the one dynamic() to the island. The form, place search,
// Vimshottari client, answer and analytics load as an async chunk; the first
// load gains only this file and the shell.
//
// Activation is element-scoped and local: no window listener and no timer, so
// a reader scrolling the article never downloads the island and the deploy's
// body pointerdown smoke loads nothing; a press, focus or submit on the shell
// does.
//
// The slot reserves the tallest state and every state fills it, so neither
// shell -> form nor a slow engine reply -> answer (or error) moves the page
// below. Natural heights measured 2026-10-10 (next dev + Playwright, the
// endpoint mocked with real engine trees and a layout-only worst case whose
// pair ended on the birth date, every date in April or October): the shell is
// 420 at every width; at 320 px wide the form is 434 (472 while it asks for a
// picked place), the engine error 538, the tallest real answer 555 and the
// worst case 576; at 360-414 px 514 at most; at 480-639 px 493; from 640 px up
// 437. Hence the planet-page check's 600 px on phones and 500 px from the sm
// breakpoint; with them the article below did not move in any state. translate="no"
// covers the shell, form and answer, which re-render on "check another"
// (tests/translate-safe-islands.test.ts).
const SHELL_PROPS = { ...RUNNING_ANTARDASHA_FORM, className: RUNNING_ANTARDASHA_FRAME } as const;

const RunningAntardashaCheck = dynamic(() => import("./RunningAntardashaCheck"), {
  ssr: false,
  loading: () => <BirthToolFormShell {...SHELL_PROPS} />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const SHELL_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: RUNNING_ANTARDASHA_FIELD_IDS.date,
  tob: RUNNING_ANTARDASHA_FIELD_IDS.time,
  place: RUNNING_ANTARDASHA_FIELD_IDS.place,
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && SHELL_FIELD_IDS[name]) || RUNNING_ANTARDASHA_FIELD_IDS.date;
}

export default function RunningAntardashaCheckLazy({
  maha,
  antar,
}: {
  maha: VimshottariLord;
  antar: VimshottariLord;
}) {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);

  // The shell's own onActivate cannot tell a keyboard focus from a touch, so
  // the slot reads the intent in the capture phase: focus follows the visitor
  // into the real form after a keyboard focus, a mouse press or a submit, and
  // never after a touch, which may be the start of a scroll.
  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(RUNNING_ANTARDASHA_FIELD_IDS.date),
      };

  return (
    <div
      translate="no"
      data-running-antardasha-slot=""
      data-page-maha={maha}
      data-page-antar={antar}
      className="flex min-h-[600px] flex-col sm:min-h-[500px]"
      {...shellIntent}
    >
      {active ? (
        <RunningAntardashaCheck maha={maha} antar={antar} focusFieldId={focusFieldId} />
      ) : (
        <BirthToolFormShell {...SHELL_PROPS} onActivate={() => setActive(true)} />
      )}
    </div>
  );
}
